<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { formatMoney } from './custom/formatters';

// Os negócios por trás de "Recebido no período" e "Previsto no período": quem
// pagou e quem tem parcela vencendo. O total daqui é o mesmo número do card,
// porque os dois saem da mesma regra no servidor (Crm::Reports::CashSql).
const props = defineProps({
  kind: { type: String, required: true },
  // Recorte extra pra mostrar no título (a linha clicada no relatório montado).
  subtitle: { type: String, default: '' },
  // Quem abre o modal sabe de onde vêm os dados: a aba Geral e o relatório
  // montado têm endpoints diferentes.
  fetcher: { type: Function, required: true },
});

const emit = defineEmits(['close']);

const router = useRouter();
const route = useRoute();

const LABELS = {
  received: 'Recebido no período',
  forecast: 'Previsto no período',
  close: 'Fechar',
  loading: 'Carregando...',
  error: 'Não foi possível carregar a lista',
  emptyReceived: 'Nenhum pagamento recebido no período',
  emptyForecast: 'Nenhum pagamento previsto no período',
  deal: 'Negociação',
  owner: 'Responsável',
  paymentsReceived: 'Pago em',
  paymentsForecast: 'Vencimento',
  amount: 'Valor no período',
  none: 'Não atribuído',
  upfront: 'À vista',
  paid: 'paga',
  overdue: 'vencida',
  open: 'em aberto',
  oneDeal: 'negociação',
  manyDeals: 'negociações',
  capped: 'Mostrando as 300 maiores. O total acima considera todas.',
};

const loading = ref(true);
const failed = ref(false);
const result = ref(null);

const isForecast = computed(() => props.kind === 'forecast');
const deals = computed(() => result.value?.deals || []);
const dealsWord = computed(() =>
  result.value?.count === 1 ? LABELS.oneDeal : LABELS.manyDeals
);

const today = new Date().toLocaleDateString('en-CA');

// A data já vem como dia (AAAA-MM-DD): vencimento não tem fuso, e passar por
// `new Date` jogaria a parcela para o dia anterior.
const formatDay = day => day.split('-').reverse().join('/');

// Só o previsto precisa dizer a situação; no recebido toda parcela está paga.
const statusOf = payment => {
  if (payment.paid) return { label: LABELS.paid, tone: 'text-emerald-500' };
  if (payment.date < today)
    return { label: LABELS.overdue, tone: 'text-n-ruby-11' };
  return { label: LABELS.open, tone: 'text-amber-500' };
};

const openDeal = deal => {
  emit('close');
  router.push({
    name: 'kanban_funnel',
    params: { accountId: route.params.accountId, funnelId: deal.funnel_id },
    query: { dealId: deal.id },
  });
};

onMounted(async () => {
  try {
    const { data } = await props.fetcher();
    result.value = data;
  } catch {
    failed.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <!-- Teleport pelo mesmo motivo do SliceDealsModal: a tela de relatórios tem
       ancestral com transform, e `fixed` se posicionaria pelo contêiner. -->
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[9990] flex items-center justify-center bg-n-alpha-black2 backdrop-blur-[4px] p-6"
      @click.self="emit('close')"
    >
      <div
        class="w-full max-w-4xl max-h-[80vh] flex flex-col rounded-xl bg-n-surface-1 border border-n-weak shadow-xl"
      >
        <header
          class="flex items-center justify-between gap-4 px-5 py-3 border-b border-n-weak"
        >
          <div class="min-w-0">
            <h2 class="text-sm font-bold text-n-slate-12 m-0 truncate">
              {{ LABELS[kind] }}
              <template v-if="subtitle">{{ `· ${subtitle}` }}</template>
            </h2>
            <p v-if="result" class="text-xs text-n-slate-11 m-0 mt-0.5">
              {{ formatMoney(result.total_cents) }}
              {{ `· ${result.count} ${dealsWord}` }}
            </p>
          </div>
          <button
            :title="LABELS.close"
            class="p-1 rounded-md border-0 bg-transparent text-n-slate-11 cursor-pointer hover:text-n-slate-12"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4 block" />
          </button>
        </header>

        <div class="flex-1 overflow-y-auto">
          <p
            v-if="loading || failed || !deals.length"
            class="px-5 py-8 text-center text-sm text-n-slate-11"
          >
            <template v-if="loading">{{ LABELS.loading }}</template>
            <template v-else-if="failed">{{ LABELS.error }}</template>
            <template v-else>
              {{ isForecast ? LABELS.emptyForecast : LABELS.emptyReceived }}
            </template>
          </p>
          <table v-else class="w-full text-sm border-collapse">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  v-for="head in [
                    LABELS.deal,
                    LABELS.owner,
                    isForecast
                      ? LABELS.paymentsForecast
                      : LABELS.paymentsReceived,
                  ]"
                  :key="head"
                  class="text-left px-5 py-2 text-xs font-semibold text-n-slate-11 uppercase"
                >
                  {{ head }}
                </th>
                <th
                  class="text-right px-5 py-2 text-xs font-semibold text-n-slate-11 uppercase whitespace-nowrap"
                >
                  {{ LABELS.amount }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="deal in deals"
                :key="deal.id"
                class="border-b border-n-weak last:border-0 cursor-pointer align-top hover:bg-n-alpha-1"
                @click="openDeal(deal)"
              >
                <td class="px-5 py-2.5">
                  <p class="m-0 text-n-slate-12 font-medium">
                    {{ deal.title }}
                  </p>
                  <p class="m-0 text-xs text-n-slate-11">
                    {{ deal.contact_name }}
                  </p>
                </td>
                <td class="px-5 py-2.5 text-n-slate-11">
                  {{ deal.assignee_name || LABELS.none }}
                </td>
                <td class="px-5 py-2.5 text-n-slate-11">
                  <p
                    v-for="payment in deal.payments"
                    :key="payment.number || 0"
                    class="m-0 text-xs leading-5 whitespace-nowrap"
                  >
                    <span class="text-n-slate-12">
                      {{ formatDay(payment.date) }}
                    </span>
                    {{
                      payment.number
                        ? `· ${payment.number}/${payment.of}`
                        : `· ${LABELS.upfront}`
                    }}
                    {{ `· ${formatMoney(payment.value_cents)}` }}
                    <span
                      v-if="isForecast"
                      class="font-medium"
                      :class="statusOf(payment).tone"
                    >
                      {{ `· ${statusOf(payment).label}` }}
                    </span>
                  </p>
                </td>
                <td
                  class="px-5 py-2.5 text-right text-n-slate-12 font-medium whitespace-nowrap"
                >
                  {{ formatMoney(deal.amount_cents) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer
          v-if="result && result.count > deals.length"
          class="px-5 py-2 border-t border-n-weak text-xs text-n-slate-11"
        >
          {{ LABELS.capped }}
        </footer>
      </div>
    </div>
  </Teleport>
</template>
